import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const GenerativeAI = () => {
  return (
    <>
      <SEO 
        title="Generative AI Solutions - Custom LLM & AI Content Generation"
        description="Transform your business with enterprise-grade Generative AI solutions. Custom LLM implementation, AI content generation, intelligent automation, and advanced AI applications for business growth."
        keywords="generative AI, large language models, LLM, AI content generation, GPT, ChatGPT enterprise, custom AI models, AI automation, enterprise AI solutions"
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-28 md:pb-20 bg-gradient-to-br from-pearl-white via-white to-blush-light/10 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #E63946 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="page-container relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blush-light/30 rounded-full mb-6 animate-fade-in">
              <svg className="w-5 h-5 text-primary-red mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              <span className="text-sm font-semibold text-primary-red">Next-Generation AI Technology</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-slide-up">
              Enterprise <span className="text-gradient">Generative AI</span> Solutions
            </h1>
            
            <p className="text-xl text-titanium leading-relaxed mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Harness the transformative power of Large Language Models and advanced AI to automate content creation, 
              enhance customer experiences, and unlock unprecedented business intelligence.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Link to="/contact" className="btn-primary">
                Start Your AI Journey
              </Link>
              <Link to="/ai-hub" className="btn-secondary">
                Explore AI Hub
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What is Generative AI Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                What is Generative AI?
              </h2>
              <p className="text-titanium mb-6">
                Generative AI represents a revolutionary leap in artificial intelligence, enabling machines to create 
                original content, from text and images to code and complex data analyses. Unlike traditional AI that 
                recognizes patterns, generative AI creates new, contextually relevant outputs.
              </p>
              <p className="text-titanium mb-6">
                Our enterprise solutions leverage state-of-the-art Large Language Models (LLMs) including GPT-4, Claude, 
                and custom-trained models to deliver unprecedented automation, creativity, and intelligence to your 
                business operations.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal mb-1">Context-Aware</h3>
                    <p className="text-sm text-titanium">Understands nuance and context</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blush-light/40 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-charcoal mb-1">Scalable</h3>
                    <p className="text-sm text-titanium">Handles enterprise workloads</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-gradient-to-br from-alabaster to-blush-light/20 rounded-2xl p-8 backdrop-blur-sm border border-platinum">
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-platinum hover:shadow-md transition-all">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h4 className="font-semibold text-charcoal">Content Generation</h4>
                    </div>
                    <p className="text-sm text-titanium">Automated document creation, reports, and marketing content</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-platinum hover:shadow-md transition-all">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                        </svg>
                      </div>
                      <h4 className="font-semibold text-charcoal">Conversational AI</h4>
                    </div>
                    <p className="text-sm text-titanium">Intelligent chatbots and virtual assistants</p>
                  </div>
                  
                  <div className="bg-white p-4 rounded-xl shadow-sm border border-platinum hover:shadow-md transition-all">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                        </svg>
                      </div>
                      <h4 className="font-semibold text-charcoal">Code Generation</h4>
                    </div>
                    <p className="text-sm text-titanium">Automated code writing and debugging assistance</p>
                  </div>

                  <div className="bg-white p-4 rounded-xl shadow-sm border border-platinum hover:shadow-md transition-all">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 bg-primary-red/10 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-5 h-5 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h4 className="font-semibold text-charcoal">Data Analysis</h4>
                    </div>
                    <p className="text-sm text-titanium">Intelligent insights and automated reporting</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Capabilities */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Comprehensive Generative AI Capabilities
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              Enterprise-grade solutions powered by the latest advancements in artificial intelligence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
                title: "Content Creation & Writing",
                description: "Generate high-quality articles, marketing copy, product descriptions, and technical documentation with AI-powered writing assistance.",
                features: ["SEO-optimized content", "Brand voice consistency", "Multilingual support"]
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-4l-5 5v-5z" />,
                title: "Intelligent Chatbots & Virtual Assistants",
                description: "Deploy advanced conversational AI that understands context, handles complex queries, and provides personalized customer experiences.",
                features: ["24/7 availability", "Multi-turn conversations", "Sentiment analysis"]
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />,
                title: "Code Generation & Development",
                description: "Accelerate software development with AI-assisted coding, automated testing, and intelligent code review capabilities.",
                features: ["Multiple languages", "Bug detection", "Code optimization"]
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
                title: "Data Synthesis & Analysis",
                description: "Transform raw data into actionable insights with AI-powered analysis, pattern recognition, and automated report generation.",
                features: ["Predictive analytics", "Trend identification", "Visual dashboards"]
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
                title: "Document Processing & Automation",
                description: "Automate document understanding, extraction, summarization, and generation for improved operational efficiency.",
                features: ["Smart extraction", "Auto-summarization", "Format conversion"]
              },
              {
                icon: <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
                title: "Custom Model Training",
                description: "Develop and deploy domain-specific AI models trained on your proprietary data for specialized business applications.",
                features: ["Fine-tuning", "Transfer learning", "Model optimization"]
              }
            ].map((capability, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-platinum group">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-red/10 to-coral-mist/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <svg className="w-7 h-7 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {capability.icon}
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-3 text-charcoal">{capability.title}</h3>
                <p className="text-titanium mb-4">{capability.description}</p>
                <ul className="space-y-2">
                  {capability.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-graphite">
                      <svg className="w-4 h-4 text-primary-red mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Powered by Leading AI Technologies
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              We leverage the most advanced language models and AI frameworks
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="card">
              <h3 className="text-xl font-bold mb-4 text-charcoal">Large Language Models (LLMs)</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-alabaster rounded-lg">
                  <span className="font-semibold text-charcoal">GPT-4 & GPT-4 Turbo</span>
                  <span className="text-sm px-3 py-1 bg-primary-red text-white rounded-full">OpenAI</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-alabaster rounded-lg">
                  <span className="font-semibold text-charcoal">Claude 3.5 Sonnet</span>
                  <span className="text-sm px-3 py-1 bg-primary-red text-white rounded-full">Anthropic</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-alabaster rounded-lg">
                  <span className="font-semibold text-charcoal">Gemini Pro</span>
                  <span className="text-sm px-3 py-1 bg-primary-red text-white rounded-full">Google</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-alabaster rounded-lg">
                  <span className="font-semibold text-charcoal">Custom Fine-tuned Models</span>
                  <span className="text-sm px-3 py-1 bg-primary-red text-white rounded-full">Proprietary</span>
                </div>
              </div>
            </div>

            <div className="card">
              <h3 className="text-xl font-bold mb-4 text-charcoal">Supporting Technologies</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-charcoal">Vector Databases</p>
                    <p className="text-sm text-titanium">Pinecone, Weaviate, Milvus for semantic search</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-charcoal">Embedding Models</p>
                    <p className="text-sm text-titanium">OpenAI Embeddings, Sentence Transformers</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-charcoal">Orchestration Frameworks</p>
                    <p className="text-sm text-titanium">LangChain, LlamaIndex for complex workflows</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="font-semibold text-charcoal">Model Serving</p>
                    <p className="text-sm text-titanium">Azure OpenAI, AWS Bedrock, GCP Vertex AI</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-alabaster to-blush-light/10">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Real-World Applications
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              Discover how organizations leverage Generative AI to transform operations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                industry: "Healthcare",
                useCase: "Medical Documentation",
                description: "Automated clinical note generation and patient summary creation",
                impact: "75% reduction in documentation time"
              },
              {
                industry: "Financial Services",
                useCase: "Report Generation",
                description: "Automated financial reports and investment analysis documents",
                impact: "90% faster report creation"
              },
              {
                industry: "E-commerce",
                useCase: "Product Content",
                description: "AI-generated product descriptions and personalized recommendations",
                impact: "3x content production rate"
              },
              {
                industry: "Legal",
                useCase: "Contract Analysis",
                description: "Automated contract review and legal document summarization",
                impact: "60% time savings"
              },
              {
                industry: "Marketing",
                useCase: "Content Creation",
                description: "AI-powered blog posts, social media content, and ad copy",
                impact: "5x content output"
              },
              {
                industry: "Customer Service",
                useCase: "Support Automation",
                description: "Intelligent chatbots handling complex customer inquiries",
                impact: "70% query resolution"
              },
              {
                industry: "Software Development",
                useCase: "Code Assistance",
                description: "AI-powered code generation and debugging support",
                impact: "40% faster development"
              },
              {
                industry: "Education",
                useCase: "Personalized Learning",
                description: "Custom curriculum generation and student assessment",
                impact: "Enhanced engagement"
              }
            ].map((useCase, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-platinum">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-primary-red">{useCase.industry}</span>
                  <svg className="w-5 h-5 text-coral-mist" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-bold mb-2 text-charcoal">{useCase.useCase}</h3>
                <p className="text-sm text-titanium mb-3">{useCase.description}</p>
                <div className="pt-3 border-t border-platinum">
                  <p className="text-xs font-semibold text-graphite">Impact: <span className="text-primary-red">{useCase.impact}</span></p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Process */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
              Our Implementation Approach
            </h2>
            <p className="text-xl text-titanium max-w-3xl mx-auto">
              A structured methodology for successful Generative AI deployment
            </p>
          </div>

          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  step: "01",
                  title: "Discovery & Assessment",
                  description: "Analyze your business needs, data infrastructure, and identify high-impact use cases for AI implementation."
                },
                {
                  step: "02",
                  title: "Solution Design",
                  description: "Design custom AI architecture, select optimal models, and create detailed implementation roadmap."
                },
                {
                  step: "03",
                  title: "Development & Training",
                  description: "Build and fine-tune models, integrate with existing systems, and conduct rigorous testing."
                },
                {
                  step: "04",
                  title: "Deployment & Optimization",
                  description: "Launch production systems, monitor performance, and continuously optimize for better results."
                }
              ].map((phase, index) => (
                <div key={index} className="relative">
                  <div className="card group hover:shadow-xl">
                    <div className="text-5xl font-bold text-primary-red/10 mb-4 group-hover:text-primary-red/20 transition-colors">
                      {phase.step}
                    </div>
                    <h3 className="text-lg font-bold mb-3 text-charcoal">{phase.title}</h3>
                    <p className="text-sm text-titanium">{phase.description}</p>
                  </div>
                  {index < 3 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <svg className="w-6 h-6 text-coral-mist" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-charcoal">
                Why Choose Generative AI?
              </h2>
              <p className="text-xl text-titanium">
                Transform your business with measurable results
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: "Unprecedented Productivity",
                  description: "Automate content creation, analysis, and decision-making processes to achieve 10x productivity gains.",
                  metric: "10x faster"
                },
                {
                  title: "Cost Optimization",
                  description: "Reduce operational costs by automating repetitive tasks and optimizing resource allocation.",
                  metric: "60% cost reduction"
                },
                {
                  title: "Enhanced Creativity",
                  description: "Generate innovative ideas, designs, and solutions that push the boundaries of what's possible.",
                  metric: "Unlimited ideation"
                },
                {
                  title: "24/7 Availability",
                  description: "Provide round-the-clock customer service, content generation, and support with AI that never sleeps.",
                  metric: "Always-on service"
                },
                {
                  title: "Scalability",
                  description: "Handle increasing workloads without proportional increases in headcount or infrastructure costs.",
                  metric: "Infinite scale"
                },
                {
                  title: "Personalization at Scale",
                  description: "Deliver personalized experiences to millions of users simultaneously with AI-driven customization.",
                  metric: "1-to-1 engagement"
                }
              ].map((benefit, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-platinum">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-bold text-charcoal flex-1">{benefit.title}</h3>
                    <span className="text-xs font-bold text-white bg-primary-red px-3 py-1 rounded-full whitespace-nowrap ml-2">
                      {benefit.metric}
                    </span>
                  </div>
                  <p className="text-titanium">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-5xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-charcoal">
                  Enterprise-Grade Security & Compliance
                </h2>
                <p className="text-titanium mb-6">
                  Your data security and regulatory compliance are our top priorities. We implement robust security 
                  measures and adhere to industry standards to protect your sensitive information.
                </p>
                <div className="space-y-4">
                  {[
                    "End-to-end encryption for data in transit and at rest",
                    "GDPR, HIPAA, and SOC 2 compliance frameworks",
                    "Private model deployment options for sensitive data",
                    "Regular security audits and penetration testing",
                    "Role-based access control (RBAC) and audit logging",
                    "Data residency and sovereignty compliance"
                  ].map((item, index) => (
                    <div key={index} className="flex items-start">
                      <svg className="w-6 h-6 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="text-titanium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-alabaster to-blush-light/20 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "ISO 27001", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
                    { label: "SOC 2 Type II", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
                    { label: "GDPR Ready", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" },
                    { label: "HIPAA Compliant", icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" }
                  ].map((cert, index) => (
                    <div key={index} className="bg-white rounded-xl p-4 text-center shadow-sm border border-platinum">
                      <svg className="w-8 h-8 text-primary-red mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={cert.icon} />
                      </svg>
                      <p className="text-sm font-semibold text-charcoal">{cert.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-primary-red to-deep-garnet text-white">
        <div className="page-container text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
              Ready to Harness the Power of Generative AI?
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Partner with us to implement cutting-edge AI solutions that transform your business operations 
              and unlock unprecedented opportunities for growth and innovation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-white text-primary-red px-8 py-4 rounded-lg text-lg font-semibold hover:bg-pearl-white transition-all duration-300 shadow-lg">
                Schedule Consultation
              </Link>
              <Link to="/ai-hub" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-primary-red transition-all duration-300">
                Explore AI Solutions
              </Link>
            </div>
            <p className="mt-6 text-sm opacity-80">
              Join 100+ enterprises already leveraging our Generative AI solutions
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default GenerativeAI;